<template>
  <div class="map-container">
    <div ref="map" class="map"></div>
    <div
      v-if="isOverlayVisible"
      :class="['map-overlay', {'in': isSpinnerVisible}]"
      style="margin: 0"
    >
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";

import Helpers from "../../../helpers.js";

import { toRaw } from 'vue';

export default {
  props: {
    googleMapApiKey: String,
    assets: Array,
  },

  data: function() {
    return {
      googleMapApiMaps: null,
      googleMapApiCore: null,
      googleMapApiMarker: null,

      googleMap: {
        map: null,
        bounds: null,
        markers: [],
        options: {
          mapTypeId: 'roadmap', //hybrid
          panControl: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          gestureHandling: 'cooperative',
          scaleControl: false,
          rotateControl: false,
          zoom: 4,
          minZoom: 2,
          maxZoom: 20,
          center: { 
            lat: 39.5, 
            lng: -98.35 
          },
          mapId: 'assetsMap',
        },
      },

      isSpinnerVisible: true,
      isInitialized: false,
      isOverlayVisible: true,
    }
  },

  setup () {
    return {
      h$: Helpers
    }
  },

  async mounted() {
    const loader = new Loader({
      apiKey: this.googleMapApiKey
    });

    this.googleMapApiCore = await loader.importLibrary("core");
    this.googleMapApiMarker = await loader.importLibrary("marker");
    this.googleMapApiMaps = await loader.importLibrary("maps");
    
    this.initializeMap();
  },

  watch: {
    assets: {
      deep: true,
      handler(assets) {
        if (this.isInitialized) {
          this.addMarkersOnMap()
        }
      }
    },
    isInitialized: {
      handler() {
        if (this.assets.length > 0) {
          this.addMarkersOnMap()
        }
      }
    }
  },

  methods: {
    initializeMap: function() {
      this.googleMap.options.mapTypeControlOptions = {
        style: this.googleMapApiMaps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: this.googleMapApiCore.ControlPosition.TOP_RIGHT
      };

      this.googleMap.options.zoomControlOptions = {
        position: this.googleMapApiCore.ControlPosition.RIGHT_BOTTOM
      };

      // Set color scheme based on current theme
      this.googleMap.options.colorScheme = (window.app.ui.theme.currentTheme === 'dark') ? this.googleMapApiCore.ColorScheme.DARK : this.googleMapApiCore.ColorScheme.LIGHT;

      this.googleMap.bounds = new this.googleMapApiCore.LatLngBounds();

      // Draw map
      this.googleMap.map = new this.googleMapApiMaps.Map(this.$refs.map, this.googleMap.options);

      this.isInitialized = true;
    },

    clearMap: function() {
      if (this.isInitialized) {
        this.googleMap.bounds = new this.googleMapApiCore.LatLngBounds();

        this.googleMap.markers.map((marker) => toRaw(marker).setMap(null));
        this.googleMap.markers = [];
      }
    },
    
    addMarkersOnMap: function() {
      // remove all map pins
      this.clearMap();

      // Add markers on map
      if (this.assets.length > 0) {
        const vmMap = this;

        // Create a mapping of statuses to ratings
        const statusRatingMap = Object.entries(vmMap.h$.fulcrumStatusRatingMap).reduce((acc, [rating, statuses]) => {
          statuses.forEach(status => acc[status] = Number(rating));
          return acc;
        }, {});

        // Add markers
        this.assets.forEach(asset => {
          const latitude = Number(asset._latitude);
          const longitude = Number(asset._longitude);

          // Skip markers without valid numeric coordinates
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return;
          }

          const marker = {
            recordID: asset.recordID,
            fulcrumRecordID: asset._record_id,
            title: asset._title,
            latitude,
            longitude,
            rating: statusRatingMap[asset._status] || 0,
            inspectionDate: asset.inspection_date,
            inspectionType: asset.inspection_type,
            commentSummary: asset.comment_summary,
            nearestAddress: asset.nearest_address,
            hasPhotos: this.hasPhotos(asset),
            // Woodpole-specific fields
            poleNumber: asset.pole_number,
            species: asset.species,
            class: asset.class,
            length: asset.length,
            damagedecayPresent: asset.damagedecay_present,
            year: asset.year,
            manufacturer: asset.manufacturer,
            maintenanceIssues: asset.maintenance_issues,
            // Padmount-specific fields
            facilityID: asset.facility_id,
            padInspectionType: asset.pad_inspection_type,
            cabinetCorrosion: asset.cabinet_corrosion,
            cabinetDent: asset.cabinet_dent,
            cabinetErosion: asset.cabinet_erosion,
            cabinetLeakingOil: asset.cabinet_leaking_oil,
            padPadIssues: asset.pad_pad_issues,
            padPadIssuesHoleSize: asset.pad_pad_issues_hole_size,
            padPadIssuesNumberOfHoles: asset.pad_pad_issues_number_of_holes,
            padmountIssues: asset.padmount_issues,
            groundMountedEquipmentType: asset.ground_mounted_equipment_type
          };

          const icon = {
            url: 'data:image/png;base64,' + this.h$.mapPinRatingMarks[marker.rating],
            scaledSize: new this.googleMapApiCore.Size(16,16),
            origin: new this.googleMapApiCore.Point(0,0),
            anchor: new this.googleMapApiCore.Point(0,0)
          };
          
          const googleMapApiMarker = new this.googleMapApiMarker.Marker({
            position: { lat: latitude, lng: longitude },
            map: vmMap.googleMap.map,
            icon: icon,
            title: marker.poleNumber
          });

          googleMapApiMarker.addListener("click", (e) => {
            this.showAssetDetails(marker);
          });

          this.googleMap.markers.push(googleMapApiMarker);

          this.googleMap.bounds.extend(new this.googleMapApiCore.LatLng(latitude, longitude));
        });

        if (this.googleMap.markers.length > 0) {
          this.googleMap.map.fitBounds(this.googleMap.bounds);

          if (this.googleMap.bounds) {
            this.googleMap.map.setCenter(this.googleMap.bounds.getCenter());
          }
        }
      }
      else {
        this.initializeMap();
      }

      this.isSpinnerVisible = false;

      setTimeout(() => {
        this.isOverlayVisible = false;
      }, 1000);
    },

    hasPhotos(item) {
      return Object.keys(item || {}).some(key => {
        return /_photos?$/.test(key) && Boolean(item[key]);
      });
    },

    showAssetDetails: function(asset) {
      vmDetailsPane.$refs.detailsPane.open({
        title: "Asset Details",
        entityType: "fulcrumAssetDetails",
        args: asset
      });
    },
  }
}
</script>
